import type { LoadMode } from './config';
import {
  type DicomJson,
  DicomWebClient,
  type InstanceRef,
  type SeriesRef,
  type StudyRef,
  tagNumber,
  tagString,
} from './dicomweb';
import { inlineLutBulkData } from './display/voilut-cs';

const TAG = {
  studyUID: '0020000D',
  seriesUID: '0020000E',
  sopUID: '00080018',
  seriesNumber: '00200011',
  instanceNumber: '00200013',
  numberOfFrames: '00280008',
  modality: '00080060',
  seriesDescription: '0008103E',
  seriesInstanceCount: '00201209',
  patientName: '00100010',
  patientId: '00100020',
  patientBirthDate: '00100030',
  patientSex: '00100040',
  studyDate: '00080020',
  studyTime: '00080030',
  studyDescription: '00081030',
  accessionNumber: '00080050',
  institutionName: '00080080',
} as const;

function toInstance(json: DicomJson, withMetadata: boolean): InstanceRef {
  return {
    sopInstanceUID: tagString(json, TAG.sopUID),
    instanceNumber: tagNumber(json, TAG.instanceNumber),
    numberOfFrames: Math.max(1, tagNumber(json, TAG.numberOfFrames, 1)),
    metadata: withMetadata ? json : undefined,
  };
}

/**
 * Loads one study's series and instances.
 * - fast: one WADO-RS series-metadata request per series (instances + every header the decoder needs).
 * - parity: one QIDO instance list per series, like the IMEXHS viewer, which then pulls each
 *   whole Part-10 file over WADO-URI and parses the header in the browser.
 */
export async function loadStudy(client: DicomWebClient, studyUID: string, mode: LoadMode): Promise<StudyRef> {
  const [study, seriesRows] = await Promise.all([client.findStudy(studyUID), client.listSeries(studyUID)]);
  const series = await Promise.all(seriesRows.map((row) => loadSeries(client, studyUID, row, mode)));
  series.sort((a, b) => a.seriesNumber - b.seriesNumber);
  const header = study ?? seriesRows[0];
  return {
    studyInstanceUID: studyUID,
    patientName: tagString(header, TAG.patientName).replace(/\^/g, ' ').trim(),
    patientId: tagString(header, TAG.patientId),
    patientBirthDate: tagString(header, TAG.patientBirthDate),
    patientSex: tagString(header, TAG.patientSex),
    studyDate: tagString(header, TAG.studyDate),
    studyTime: tagString(header, TAG.studyTime),
    studyDescription: tagString(header, TAG.studyDescription),
    accessionNumber: tagString(header, TAG.accessionNumber),
    institutionName: tagString(header, TAG.institutionName),
    series: series.filter((s) => s.instances.length > 0),
  };
}

/** One series of a QIDO series row: its instances (and, in fast mode, their metadata). */
export async function loadSeries(client: DicomWebClient, studyUID: string, row: DicomJson, mode: LoadMode): Promise<SeriesRef> {
  const seriesUID = tagString(row, TAG.seriesUID);
  const rows = mode === 'fast'
    ? await client.seriesMetadata(studyUID, seriesUID)
    : await client.listInstances(studyUID, seriesUID);
  if (mode === 'fast') await inlineLutBulkData(client, rows);
  const instances = keepImageInstances(rows.map((json) => toInstance(json, mode === 'fast')))
    .sort((a, b) => a.instanceNumber - b.instanceNumber);
  const first = mode === 'fast' ? rows[0] : row;
  return {
    studyInstanceUID: studyUID,
    seriesInstanceUID: seriesUID,
    seriesNumber: tagNumber(row, TAG.seriesNumber),
    modality: tagString(row, TAG.modality) || tagString(first, TAG.modality),
    description: tagString(row, TAG.seriesDescription),
    instanceCount: instances.length,
    instances,
  };
}

/** Series never listed or auto-placed (IMEXHS `PR`, `KO`, `SEG`, `REPORT` sets). */
export const IGNORED_MODALITIES = new Set(['PR', 'KO', 'SEG', 'REPORT']);
/** Report modalities: shown by the SR renderer, or disabled when it is off. */
export const DISABLED_MODALITIES = new Set(['SR']);
const SOP_CLASS_TAG = '00080016';
export const IGNORED_SOP_PREFIXES = ['1.2.840.10008.5.1.4.1.1.11.', '1.2.840.10008.5.1.4.1.1.88.59', '1.2.840.10008.5.1.4.1.1.66.4'];
export const SR_SOP_PREFIX = '1.2.840.10008.5.1.4.1.1.88.';

/** SOP Class UID of an instance, when its metadata is known (fast mode). */
export function sopClassOf(instance: InstanceRef): string {
  return instance.metadata ? tagString(instance.metadata, SOP_CLASS_TAG) : '';
}

/**
 * SOP classes that carry no displayable pixels: raw data, registrations and fiducials, waveforms,
 * RT structure sets / plans / records, real-world value maps and vendor-private non-image objects
 * (Siemens CSA, Philips). A series that mixes them with images keeps only its images, so the frame
 * counter and the scroll range are right and no frame fails to decode.
 */
export const NON_IMAGE_SOP_CLASSES = new Set([
  '1.2.840.10008.5.1.4.1.1.66', '1.2.840.10008.5.1.4.1.1.66.1', '1.2.840.10008.5.1.4.1.1.66.2',
  '1.2.840.10008.5.1.4.1.1.66.3', '1.2.840.10008.5.1.4.1.1.66.5', '1.2.840.10008.5.1.4.1.1.67',
  '1.2.840.10008.5.1.4.1.1.481.3', '1.2.840.10008.5.1.4.1.1.481.4', '1.2.840.10008.5.1.4.1.1.481.5',
  '1.2.840.10008.5.1.4.1.1.481.6', '1.2.840.10008.5.1.4.1.1.481.7', '1.2.840.10008.5.1.4.1.1.481.8',
  '1.2.840.10008.5.1.4.1.1.481.9',
]);
export const NON_IMAGE_SOP_PREFIXES = ['1.2.840.10008.5.1.4.1.1.9.', '1.3.12.2.1107.5.9.1', '1.3.46.670589.2.5.1.1'];

/** Modalities with nothing this viewer can draw (waveforms, audio, plans, registrations, 3D models...). */
export const UNSUPPORTED_MODALITIES = new Set([
  'SG', 'AU', 'ECG', 'EEG', 'EMG', 'EOG', 'EPS', 'HD', 'POS', 'RESP', 'ASMT', 'FID', 'M3D', 'PLAN', 'REG',
  'RTPLAN', 'RTRECORD', 'RTSTRUCT', 'RWV',
]);
export const UNSUPPORTED_SERIES_TITLE = 'Serie no compatible con el visor';

export function isNonImageSop(sop: string): boolean {
  return NON_IMAGE_SOP_CLASSES.has(sop) || NON_IMAGE_SOP_PREFIXES.some((prefix) => sop.startsWith(prefix));
}

/** Drops non-image instances from a series that also has images (a series of only non-image objects is kept whole). */
export function keepImageInstances(instances: InstanceRef[]): InstanceRef[] {
  const images = instances.filter((instance) => !isNonImageSop(sopClassOf(instance)));
  return images.length > 0 ? images : instances;
}

/** Listed (so the user knows it exists) but never opened: nothing in it can be drawn here. */
export function isUnsupportedSeries(series: SeriesRef): boolean {
  if (UNSUPPORTED_MODALITIES.has(series.modality)) return true;
  return series.instances.length > 0 && series.instances.every((instance) => isNonImageSop(sopClassOf(instance)));
}

/** Presentation states, key-object notes, segmentations: never shown as a series. */
export function isIgnoredSeries(series: SeriesRef): boolean {
  if (IGNORED_MODALITIES.has(series.modality)) return true;
  const sop = series.instances[0] ? sopClassOf(series.instances[0]) : '';
  return IGNORED_SOP_PREFIXES.some((prefix) => sop.startsWith(prefix));
}

/**
 * Structured reports are listed and shown through the SR renderer (src/display/sr.ts) while
 * `structuredReportsEnabled` (IMEXHS config, default true) is on; with it off they are listed disabled.
 */
function structuredReportsEnabled(): boolean {
  const config = window.VIEWER_CONFIG as unknown as Record<string, unknown> | undefined;
  return config?.structuredReportsEnabled !== false;
}

/** Listed but not viewable: unsupported series, and structured reports when their renderer is turned off. */
export function isDisabledSeries(series: SeriesRef): boolean {
  if (isUnsupportedSeries(series)) return true;
  const sop = series.instances[0] ? sopClassOf(series.instances[0]) : '';
  const isReport = DISABLED_MODALITIES.has(series.modality) || sop.startsWith(SR_SOP_PREFIX);
  return isReport && !structuredReportsEnabled();
}

/** A series a viewport can display (what the layout fills cells with). */
export function isImageSeries(series: SeriesRef): boolean {
  return !isIgnoredSeries(series) && !isDisabledSeries(series);
}
